/**
 * useSSEConnection — Shared SSE connection engine.
 *
 * Encapsulates EventSource lifecycle, nonce-based auth, exponential
 * backoff reconnection, and the 6 core SSE event handlers (connected,
 * chunk, message, typing, error, ping).
 *
 * Used by useChat (companion) and useWilliamsChat (Williams).
 */

import { useRef, useCallback, useEffect } from 'react';
import { getSSENonce } from '@/services/chatApi';
import { API_BASE_URL } from '@/lib/constants';
import type { ChatMessageState } from '@/types/chat';

const SSE_BASE_URL = API_BASE_URL;
const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY = 3000;

/**
 * Default-framed payloads from sseStreams.pushToFamily land on the default
 * 'message' listener alongside the chat backend's assistant turns. Branching
 * on these explicit event names — rather than any payload with a top-level
 * `type` field — keeps a future assistant message that happens to carry a
 * `type` key from being swallowed by the handoff routing.
 */
const HANDOFF_EVENT_TYPES = new Set<string>([
  'trfer_message',
  'trfer_joined',
  'handback',
  'reassigned',
  'ai_cancelled',
  'handoff_sla_breached',
  'handoff_initiated',
  // Translation pipeline: per-message metadata refresh (e.g. after retry).
  'message_updated',
  // Ghost-claim releaser: TRFer went silent past the heartbeat threshold,
  // claim auto-released back to `open`, family resumed with its companion.
  'trfer_idle_released',
]);

// ─── Types ──────────────────────────────────────────────────────

export interface SSEHandlers {
  onStatusChange: (status: 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error') => void;
  onTyping: (typing: boolean) => void;
  onStreamingChange: (streaming: boolean) => void;
  onError: (error: string) => void;
  onMessage: (message: ChatMessageState) => void;
  /** Called for any SSE event type not handled by the core handlers. */
  onCustomEvent?: (eventName: string, data: unknown) => void;
}

export interface SSEConnectionConfig {
  /** SSE URL path prefix, e.g., '/api/chat/stream' or '/api/trfer/williams/stream' */
  ssePathPrefix: string;
  /** Whether the connection should auto-connect. Defaults to true. */
  enabled?: boolean;
  /** List of extra SSE event names to listen for (forwarded to onCustomEvent). */
  customEvents?: string[];
}

// ─── Hook ───────────────────────────────────────────────────────

export function useSSEConnection(
  conversationId: string,
  handlers: SSEHandlers,
  config: SSEConnectionConfig,
) {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const streamingMessageIdRef = useRef<string | null>(null);

  // Keep refs for latest values to avoid stale closures
  const conversationIdRef = useRef(conversationId);
  const handlersRef = useRef(handlers);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
  }, []);

  const connect = useCallback(async () => {
    if (eventSourceRef.current) return;

    const convId = conversationIdRef.current;
    if (!convId) return;

    const h = handlersRef.current;
    h.onStatusChange('connecting');

    let nonce: string;
    try {
      nonce = await getSSENonce();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const message =
        status === 401
          ? 'Your session has expired. Please sign in again.'
          : status === 429
          ? 'Too many connection attempts. Please wait a moment and try again.'
          : 'Couldn’t open a secure connection. Please refresh the page.';
      h.onError(message);
      h.onStatusChange('error');
      return;
    }

    // Re-check after async gap. Two things can have changed during the
    // nonce fetch: another connect() may have already raced ahead and
    // installed an EventSource, OR the conversationId may have switched
    // (ChatPage auto-switches from the initial UUID to whatever the
    // conversations query resolved). In either case the EventSource we
    // were about to open is stale before it opens — abort.
    if (eventSourceRef.current || conversationIdRef.current !== convId) return;

    const url = `${SSE_BASE_URL}${config.ssePathPrefix}/${convId}?token=${nonce}`;
    const es = new EventSource(url);

    // ── connected ──
    es.addEventListener('connected', () => {
      handlersRef.current.onStatusChange('connected');
      reconnectAttemptsRef.current = 0;
    });

    // ── chunk (streaming) ──
    es.addEventListener('chunk', (event: MessageEvent) => {
      try {
        JSON.parse(event.data); // validate parse
        if (!streamingMessageIdRef.current) {
          streamingMessageIdRef.current = 'pending';
          handlersRef.current.onTyping(false);
          handlersRef.current.onStreamingChange(true);
        }
      } catch (err) {
        console.error('Failed to parse chunk:', err);
      }
    });

    // ── message (final) ──
    // The chat backend writes assistant turns as `event: message\ndata:...`,
    // which lands here. The handoff backend (sseStreams.pushToFamily) writes
    // default-framed `data:{type:'...'}\n\n` payloads onto the SAME socket —
    // those also land here. We branch on `data.type` against the
    // HANDOFF_EVENT_TYPES whitelist BEFORE treating the payload as an
    // assistant message, so future assistant fields named `type` cannot
    // silently re-route into the handoff path.
    es.addEventListener('message', (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data && typeof data === 'object' && typeof data.type === 'string' && HANDOFF_EVENT_TYPES.has(data.type)) {
          handlersRef.current.onCustomEvent?.(data.type, data);
          return;
        }
        handlersRef.current.onMessage({
          id: data.id,
          role: data.role,
          content: data.content,
          timestamp: data.timestamp,
          status: 'complete',
          metadata: {
            agent: data.agent,
            ...(data.metadata as Record<string, unknown> | undefined),
          },
        });

        streamingMessageIdRef.current = null;
        handlersRef.current.onStreamingChange(false);
        handlersRef.current.onTyping(false);
      } catch (err) {
        console.error('Failed to parse message:', err);
      }
    });

    // ── typing ──
    es.addEventListener('typing', (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        handlersRef.current.onTyping(data.typing);
      } catch (err) {
        console.error('Failed to parse typing:', err);
      }
    });

    // ── error ──
    es.addEventListener('error', (event: MessageEvent) => {
      if (event.data) {
        try {
          const data = JSON.parse(event.data);
          handlersRef.current.onError(data.error);
          if (streamingMessageIdRef.current) {
            streamingMessageIdRef.current = null;
            handlersRef.current.onStreamingChange(false);
          }
          handlersRef.current.onTyping(false);
        } catch {
          // Browser-level error
        }
      }
    });

    // ── ping (keepalive, no-op) ──
    es.addEventListener('ping', () => {});

    // ── Custom events (intent, routing, safety_check, escalation, action, etc.) ──
    if (config.customEvents) {
      for (const eventName of config.customEvents) {
        es.addEventListener(eventName, (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data);
            handlersRef.current.onCustomEvent?.(eventName, data);
          } catch (err) {
            console.error(`Failed to parse ${eventName}:`, err);
          }
        });
      }
    }

    // ── Browser-level connection error / close ──
    es.onerror = () => {
      if (streamingMessageIdRef.current) {
        streamingMessageIdRef.current = null;
        handlersRef.current.onStreamingChange(false);
        handlersRef.current.onTyping(false);
      }
      handlersRef.current.onStatusChange('disconnected');
      es.close();
      eventSourceRef.current = null;

      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current += 1;
        const delay =
          BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current - 1);
        console.warn(
          `SSE lost. Reconnecting (${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS}) in ${delay / 1000}s...`
        );
        handlersRef.current.onStatusChange('connecting');
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      } else {
        handlersRef.current.onError('Connection lost. Please refresh the page.');
        handlersRef.current.onStatusChange('error');
      }
    };

    eventSourceRef.current = es;
  }, [config.ssePathPrefix, config.customEvents]);

  // Auto-connect / disconnect lifecycle.
  //
  // `conversationId` is in the deps so the effect re-runs when ChatPage
  // switches conversations (cold-mount auto-switch from a placeholder UUID
  // to the conversations-query result, or `Start new conversation`). On
  // re-run, cleanup calls disconnect() to close the old EventSource, then
  // the effect body calls connect() which reads the now-updated
  // conversationIdRef.
  const enabled = config.enabled ?? true;
  useEffect(() => {
    if (!enabled) return;
    connect();
    return () => disconnect();
  }, [enabled, conversationId, connect, disconnect]);

  return { connect, disconnect };
}
